import { useState, type ReactNode } from 'react'
import { Bell, BellOff } from 'lucide-react'
import { ActionTrigger, type ActionAs } from './ActionBar'
import { Button, Sheet } from './ui'

const iso = (d: Date) => d.toISOString()
const at = (d: Date, h: number, m = 0) => {
  const x = new Date(d)
  x.setHours(h, m, 0, 0)
  return x
}
const presets = (): { label: string; when: Date }[] => {
  const now = new Date()
  const inHour = new Date(now.getTime() + 60 * 60 * 1000)
  const evening = at(now, 18)
  const tomorrow = at(new Date(now.getTime() + 86_400_000), 9)
  const monday = new Date(now)
  monday.setDate(now.getDate() + ((8 - now.getDay()) % 7 || 7))
  return [
    { label: 'In an hour', when: inHour },
    ...(evening > now ? [{ label: 'This evening (18:00)', when: evening }] : []),
    { label: 'Tomorrow 09:00', when: tomorrow },
    { label: 'Monday 09:00', when: at(monday, 9) },
  ]
}
const toLocalInput = (isoValue: string) => {
  const d = new Date(isoValue)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export const fmtRemind = (isoValue: string) => {
  const d = new Date(isoValue)
  const today = new Date()
  const sameDay = d.toDateString() === today.toDateString()
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  return sameDay
    ? time
    : `${d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} ${time}`
}

/**
 * One-off reminder on any item: a chip showing when, and a sheet with a few
 * presets plus a free date-time. Stores RFC 3339 UTC in spec.remind_at; the
 * server pushes at that time to every device with notifications on.
 */
export const RemindPicker = ({
  value,
  onChange,
  fired,
  as,
  note,
}: {
  value?: string
  onChange: (v: string | null) => void
  fired?: boolean
  as?: ActionAs
  /** a quiet line under the sheet's explanation (a task's "Rings on Keira’s phone too") */
  note?: ReactNode
}) => {
  const [open, setOpen] = useState(false)
  const [custom, setCustom] = useState(() => (value ? toLocalInput(value) : ''))
  const set = (d: Date | null) => {
    onChange(d ? iso(d) : null)
    setOpen(false)
  }
  const past = value ? new Date(value) < new Date() : false
  return (
    <>
      <ActionTrigger
        as={as}
        icon={Bell}
        pressed={!!value}
        label={value ? (past || fired ? `Reminded ${fmtRemind(value)}` : fmtRemind(value)) : 'Remind me'}
        onClick={() => setOpen(true)}
        testid="remind"
        title="Remind me: a notification at a time you pick"
      />
      {open && (
        <Sheet open onClose={() => setOpen(false)} title="Remind me">
          <p className="text-xs text-faint mb-3">
            A notification on every device with notifications on. Pick a moment, or set your own.
          </p>
          {note}
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="remind-presets">
            {presets().map((p) => (
              <Button key={p.label} variant="chip" onClick={() => set(p.when)}>
                {p.label}
              </Button>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="datetime-local"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              className="flex-1"
              aria-label="Reminder time"
              data-testid="remind-custom"
            />
            <Button
              variant="primary"
              disabled={!custom}
              onClick={() => custom && set(new Date(custom))}
              data-testid="remind-save"
            >
              Set
            </Button>
          </div>
          {value && (
            <Button className="mt-3" onClick={() => set(null)} data-testid="remind-clear">
              <BellOff size={14} className="mr-1 inline" aria-hidden /> Clear reminder
            </Button>
          )}
        </Sheet>
      )}
    </>
  )
}
